import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserId } from '@/components/AuthProvider';
import { Avatar } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './MessagesLayout.module.css';

type Room = {
  id: string;
  booking_id: string;
  other_name: string;
  service_name: string;
  status: string;
};

export default function MessagesLayout({ area }: { area: 'client' | 'provider' }) {
  const userId = useUserId();
  const { roomId } = useParams<{ roomId?: string }>();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;

    const { data } = await supabase
      .from('chat_rooms')
      .select(
        `id, booking_id, updated_at,
         bookings(services(name), status),
         client:profiles!chat_rooms_client_id_fkey(id, full_name),
         provider:profiles!chat_rooms_provider_id_fkey(id, full_name)`
      )
      .or(`client_id.eq.${userId},provider_id.eq.${userId}`)
      .order('updated_at', { ascending: false });

    setRooms(
      (data ?? []).map((row: any) => {
        const viewerIsClient = row.client?.id === userId;
        const other = viewerIsClient ? row.provider : row.client;
        return {
          id: row.id,
          booking_id: row.booking_id,
          other_name: other?.full_name ?? (viewerIsClient ? 'Prestataire' : 'Client'),
          service_name: row.bookings?.services?.name ?? 'Service',
          status: row.bookings?.status ?? '',
        };
      })
    );
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Refresh the list ordering when messages arrive.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`${area}-messages-list`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        () => void load()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'chat_rooms' },
        () => void load()
      )
      .subscribe();

    return () => {
      void channel.unsubscribe();
    };
  }, [area, load, userId]);

  const basePath = area === 'client' ? '/app/messages' : '/pro/messages';

  return (
    <div className={`${styles.layout} ${roomId ? styles.layoutWithRoom : ''}`}>
      <aside className={styles.listPane}>
        <header className={styles.listHeader}>
          <h1 className={styles.listTitle}>Messages</h1>
          <p className={styles.listCount}>
            {rooms.length} conversation{rooms.length > 1 ? 's' : ''}
          </p>
        </header>

        {loading ? (
          <Spinner block />
        ) : rooms.length === 0 ? (
          <div className={styles.listEmpty}>
            <span className={styles.listEmptyIcon}>
              <MessageSquare size={30} />
            </span>
            <p>Aucune conversation</p>
          </div>
        ) : (
          <ul className={styles.roomList}>
            {rooms.map((room) => (
              <li key={room.id}>
                <NavLink
                  to={`${basePath}/${room.id}`}
                  className={({ isActive }) =>
                    isActive ? `${styles.roomCard} ${styles.roomCardActive}` : styles.roomCard
                  }
                >
                  <Avatar name={room.other_name} size={50} tone="orange" />
                  <div className={styles.roomInfo}>
                    <p className={styles.roomName}>{room.other_name}</p>
                    <p className={styles.roomService}>{room.service_name}</p>
                  </div>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className={styles.chatPane}>
        <Outlet />
      </section>
    </div>
  );
}
